import { useEffect, useState } from "react";
import { Sparkles, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [editCamp, setEditCamp] = useState(null);
  const [editFest, setEditFest] = useState(null);

  const load = () => {
    api.get("/campaigns/all").then(r => setCampaigns(r.data));
    api.get("/festivals").then(r => setFestivals(r.data));
    api.get("/discount-rules").then(r => setDiscounts(r.data));
  };
  useEffect(load, []);

  const toggleCampaign = async (c, active) => {
    await api.post("/campaigns", { ...c, active });
    toast.success(active ? "Activated" : "Deactivated");
    load();
  };

  const toggleFest = async (f, active) => {
    await api.post("/festivals", { ...f, active });
    toast.success(active ? "Activated" : "Deactivated");
    load();
  };

  const toggleDiscount = async (d, active) => {
    await api.post("/discount-rules", { ...d, active });
    toast.success(active ? "Activated" : "Deactivated");
    load();
  };

  const saveCamp = async () => {
    await api.post("/campaigns", editCamp);
    toast.success("Saved");
    setEditCamp(null);
    load();
  };

  const saveFest = async () => {
    await api.post("/festivals", editFest);
    toast.success("Saved");
    setEditFest(null);
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-4xl">Campaigns & Discounts</h1>
        <p className="text-[#8A7970] mt-1">Anniversary special, festival rates, long-stay discounts.</p>
      </div>

      {/* Campaigns */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Campaigns</h2>
          <Button onClick={() => setEditCamp({ id: `camp_${Date.now()}`, name: "", tagline: "", discount_type: "percentage", discount_value: 10, start_date: "", end_date: "", active: true, applies_to: "all" })} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="new-campaign-btn">
            <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5}/> New Campaign
          </Button>
        </div>
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-[#FDFBF7] rounded-xl p-5 border border-[#E7C2A6]/40 flex items-center justify-between" data-testid={`campaign-${c.id}`}>
              <div className="flex-1">
                <div className="font-serif text-xl">{c.name}</div>
                <div className="text-sm text-[#8A7970] mt-1">{c.tagline}</div>
                <div className="text-xs text-[#8A7970] mt-2">{c.start_date} → {c.end_date} · {c.discount_type === "nth_night_free" ? `Every ${c.discount_value}nd night free` : `${c.discount_value}${c.discount_type === "percentage" ? "%" : "₹"} off`}</div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={c.active} onCheckedChange={(v) => toggleCampaign(c, v)} data-testid={`toggle-camp-${c.id}`}/>
                <Button variant="outline" size="sm" onClick={() => setEditCamp(c)} data-testid={`edit-camp-${c.id}`}>Edit</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Festivals */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Festival Rates</h2>
          <Button onClick={() => setEditFest({ id: `fest_${Date.now()}`, name: "", start_date: "", end_date: "", price_multiplier: 1.5, active: true })} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="new-festival-btn">Add Festival</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {festivals.map(f => (
            <div key={f.id} className="bg-[#FDFBF7] rounded-xl p-5 border border-[#E7C2A6]/40" data-testid={`festival-${f.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-lg">{f.name}</div>
                  <div className="text-xs text-[#8A7970] mt-1">{f.start_date} → {f.end_date}</div>
                  <div className="text-sm mt-2">Rate × <b>{f.price_multiplier}</b></div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={f.active} onCheckedChange={(v) => toggleFest(f, v)} data-testid={`toggle-fest-${f.id}`}/>
                  <Button variant="outline" size="sm" onClick={() => setEditFest(f)}>Edit</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Discount Rules */}
      <div>
        <h2 className="font-serif text-2xl mb-4">Auto-Discount Rules</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {discounts.map(d => (
            <div key={d.id} className="bg-[#FDFBF7] rounded-xl p-5 border border-[#E7C2A6]/40 flex items-center justify-between" data-testid={`discount-${d.id}`}>
              <div>
                <div className="flex items-center gap-2"><Percent className="w-4 h-4 text-[#B84F3E]" strokeWidth={1.5}/><span className="font-medium">{d.name}</span></div>
                <div className="text-xs text-[#8A7970] mt-1.5">{d.discount_percentage}% off · {d.condition_type.replace(/_/g,' ')} ≥ {d.condition_value}</div>
              </div>
              <Switch checked={d.active} onCheckedChange={(v) => toggleDiscount(d, v)} data-testid={`toggle-disc-${d.id}`}/>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Campaign */}
      <Dialog open={!!editCamp} onOpenChange={(o) => !o && setEditCamp(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-campaign-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Campaign</DialogTitle></DialogHeader>
          {editCamp && (
            <div className="space-y-3">
              <div><label className="text-xs text-[#8A7970]">Name</label><Input value={editCamp.name} onChange={(e)=>setEditCamp({...editCamp,name:e.target.value})}/></div>
              <div><label className="text-xs text-[#8A7970]">Tagline (shown on landing page)</label><Input value={editCamp.tagline} onChange={(e)=>setEditCamp({...editCamp,tagline:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#8A7970]">Start Date</label><Input type="date" value={editCamp.start_date} onChange={(e)=>setEditCamp({...editCamp,start_date:e.target.value})}/></div>
                <div><label className="text-xs text-[#8A7970]">End Date</label><Input type="date" value={editCamp.end_date} onChange={(e)=>setEditCamp({...editCamp,end_date:e.target.value})}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#8A7970]">Type</label>
                  <select className="w-full h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={editCamp.discount_type} onChange={(e)=>setEditCamp({...editCamp,discount_type:e.target.value})}>
                    <option value="percentage">% off</option>
                    <option value="flat">₹ flat off</option>
                    <option value="nth_night_free">Nth night free</option>
                  </select>
                </div>
                <div><label className="text-xs text-[#8A7970]">Value</label><Input type="number" value={editCamp.discount_value} onChange={(e)=>setEditCamp({...editCamp,discount_value:parseInt(e.target.value)||0})}/></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCamp(null)}>Cancel</Button>
            <Button onClick={saveCamp} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="save-camp-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Festival */}
      <Dialog open={!!editFest} onOpenChange={(o) => !o && setEditFest(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-festival-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Festival Period</DialogTitle></DialogHeader>
          {editFest && (
            <div className="space-y-3">
              <div><label className="text-xs text-[#8A7970]">Name</label><Input value={editFest.name} onChange={(e)=>setEditFest({...editFest,name:e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#8A7970]">Start Date</label><Input type="date" value={editFest.start_date} onChange={(e)=>setEditFest({...editFest,start_date:e.target.value})}/></div>
                <div><label className="text-xs text-[#8A7970]">End Date</label><Input type="date" value={editFest.end_date} onChange={(e)=>setEditFest({...editFest,end_date:e.target.value})}/></div>
              </div>
              <div><label className="text-xs text-[#8A7970]">Price Multiplier (e.g., 1.5 = 50% more)</label><Input type="number" step="0.1" value={editFest.price_multiplier} onChange={(e)=>setEditFest({...editFest,price_multiplier:parseFloat(e.target.value)||1})}/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFest(null)}>Cancel</Button>
            <Button onClick={saveFest} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="save-fest-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
